import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { POTS, type PotKind } from "../rules/symbols";
import { IMPACT_TUNING, POT_ERUPTION_TUNING } from "../tuning/featureTuning";
import { ART_SCALE, IMPACT_LAYOUT, POT_ERUPTION_LAYOUT, POTS_LAYOUT } from "./layout";

/**
 * The artist's impact (`impact.riv`, played as its Spine conversion): its `hit` trigger plays the burst once (a second;
 * again mid-burst, it starts over), its `color` a number per pot. Idle, it draws nothing.
 */
export const IMPACT_RIVE = {
  trigger: "hit",
  color: "color",
  colors: { life: 1, multi: 2, grow: 3 } satisfies Record<PotKind, number>,
} as const;

/**
 * The artist's pot eruption (`pot_eruption.riv`, played as its Spine conversion): its `erupt` trigger plays it once
 * (0.9 s; again, from its start) — a crater flash, a ring of fire, jets up and off the screen, puffs, sparks, a glow
 * over the pot — its `color` a number per pot (its size with it). Idle, it draws nothing.
 */
export const POT_ERUPTION_RIVE = {
  trigger: "erupt",
  color: "color",
  colors: { life: 1, multi: 2, grow: 3 } satisfies Record<PotKind, number>,
} as const;

/** A burst on a pot's opening (`PotBursts`): its Rive file's inputs, its artboard's place on the opening, its length. */
export interface PotBurst {
  /** Its sprites' label (`<label>:<pot>`), and the line logged as its art is in. */
  label: string;
  loaded: string;
  rive: { trigger: string; color: string; colors: Readonly<Record<PotKind, number>> };
  /** Its artboard (the mock-up's pixels) and its point on the pot's opening. */
  artboard: { width: number; height: number };
  anchor: { x: number; y: number };
  /** How long it is drawn after its trigger (engine seconds), then hidden. */
  seconds: number;
}

/** The impact as a pot's light reaches it: a flash the potion's level switches under, a glow, rays, a splash. */
export const IMPACT_BURST: PotBurst = {
  label: "Impact",
  loaded: "[PotImpacts] the impacts are in",
  rive: IMPACT_RIVE,
  artboard: IMPACT_LAYOUT.artboard,
  anchor: IMPACT_LAYOUT.hit,
  seconds: IMPACT_TUNING.seconds,
};

/** A pot erupting (a booster lighting, LIFE's refill, MULTI's badges): its jets leave over the plaques and the logo. */
export const ERUPTION_BURST: PotBurst = {
  label: "Eruption",
  loaded: "[PotEruptions] the eruptions are in",
  rive: POT_ERUPTION_RIVE,
  artboard: POT_ERUPTION_LAYOUT.artboard,
  anchor: POT_ERUPTION_LAYOUT.base,
  seconds: POT_ERUPTION_TUNING.seconds,
};

/** What a burst asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface BurstArt {
  readonly texture: Texture;
  resize(scale: number): void;
  setNumber(name: string, value: number): void;
  trigger(name: string): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

interface Burst {
  art: BurstArt;
  sprite: Sprite;
  /** Engine seconds of its burst still to draw (0: idle, hidden). */
  left: number;
}

/**
 * The artist's bursts on the pots' openings, one kind each (`PotBurst`): the impacts, the eruptions. One artboard per pot
 * in its colour (the file loaded once), at the mock-up's scale, shown and drawn on the UI's runner for its burst after
 * it plays, at the tuning's frame rate, then hidden: never work a round waits for. Nothing plays before they are in.
 */
export class PotBursts extends Container {
  private readonly burst: PotBurst;
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private bursts: Readonly<Record<PotKind, Burst>> | null = null;
  private readonly played: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
  private drawing = false;
  /** Device pixels per design unit where the bursts play (their canvases' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { burst: PotBurst; ambientRunner: PresentationTaskRunner; log: Log }) {
    super({ label: `${input.burst.label}s` });
    this.burst = input.burst;
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's bursts, loaded: an artboard per pot, each in its pot's colour on its opening. */
  public setArt(arts: Readonly<Record<PotKind, BurstArt>>): void {
    if (this.destroyed || this.bursts) {
      for (const kind of POTS) arts[kind].dispose();
      return;
    }
    const { artboard, anchor, rive, label } = this.burst;
    const build = (kind: PotKind): Burst => {
      const art = arts[kind];
      art.setNumber(rive.color, rive.colors[kind]);
      art.resize(ART_SCALE * this.pixelsPerUnit);
      // Its point on the opening, at the mock-up's scale.
      const sprite = new Sprite({ texture: art.texture, anchor: { x: anchor.x / artboard.width, y: anchor.y / artboard.height }, label: `${label}:${kind}` });
      sprite.setSize(artboard.width * ART_SCALE, artboard.height * ART_SCALE);
      sprite.position.set(POTS_LAYOUT.opening[kind].x, POTS_LAYOUT.opening[kind].y);
      sprite.visible = false;
      this.addChild(sprite);
      return { art, sprite, left: 0 };
    };
    this.bursts = { life: build("life"), multi: build("multi"), grow: build("grow") };
    this.log(this.burst.loaded);
  }

  /** Whether the bursts are in. */
  public get ready(): boolean {
    return this.bursts !== null;
  }

  /** How many times each pot's burst has played, and which play now (the dev hook's read). */
  public get state(): { played: Readonly<Record<PotKind, number>>; playing: Readonly<Record<PotKind, boolean>> } {
    const playing = (kind: PotKind): boolean => (this.bursts?.[kind].left ?? 0) > 0;
    return { played: { ...this.played }, playing: { life: playing("life"), multi: playing("multi"), grow: playing("grow") } };
  }

  /** Device pixels per design unit where the bursts play: their canvases are drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    if (this.bursts) for (const kind of POTS) this.bursts[kind].art.resize(ART_SCALE * pixelsPerUnit);
  }

  /** `pot`'s burst plays (again from its start if it plays already); nothing before the bursts are in. */
  public play(pot: PotKind): void {
    const burst = this.bursts?.[pot];
    if (!burst || this.destroyed) return;
    this.played[pot] += 1;
    burst.art.trigger(this.burst.rive.trigger);
    burst.art.render(0);
    burst.sprite.visible = true;
    burst.left = this.burst.seconds;
    this.draw();
  }

  public override destroy(options?: DestroyOptions): void {
    const bursts = this.bursts;
    this.bursts = null;
    if (bursts) for (const kind of POTS) bursts[kind].art.dispose();
    super.destroy(options);
  }

  /** The playing bursts drawn at their own pace until each is over, then hidden. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          for (const kind of POTS) {
            const burst = this.bursts?.[kind];
            if (!burst || burst.left <= 0) continue;
            burst.art.advance(delta);
            burst.left = Math.max(0, burst.left - delta);
            if (burst.left === 0) burst.sprite.visible = false;
          }
        },
        () => this.destroyed || !this.bursts || POTS.every((kind) => this.bursts![kind].left <= 0),
      )
      .then(() => {
        this.drawing = false;
        // One played as the last ended.
        if (!this.destroyed && this.bursts && POTS.some((kind) => this.bursts![kind].left > 0)) this.draw();
      });
  }
}
